import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";
import { nav, brand } from "@/data/siteContent";
import { scrollToSection } from "@/hooks/useLenis";
import MagneticButton from "@/components/MagneticButton";

const FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Navbar({ ready }) {
  const barRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const lastScroll = useRef(0);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    if (!ready) return undefined;
    const reduced = prefersReducedMotion();
    if (reduced) {
      gsap.set(barRef.current, { y: 0, opacity: 1 });
    } else {
      gsap.fromTo(
        barRef.current,
        { y: -80, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out", delay: 0.2 }
      );
    }

    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      const hide = y > lastScroll.current && y > 300;
      if (reduced) {
        gsap.set(barRef.current, { y: hide ? -120 : 0 });
      } else if (hide) {
        gsap.to(barRef.current, { y: -120, duration: 0.5, ease: "power2.out" });
      } else {
        gsap.to(barRef.current, { y: 0, duration: 0.5, ease: "power2.out" });
      }
      lastScroll.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ready]);

  // active-section highlighting
  useEffect(() => {
    const ids = nav.links.map((l) => l.href.replace("#", ""));
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!sections.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(`#${entry.target.id}`);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [ready]);

  // Escape close + lightweight focus trap while mobile menu is open
  useEffect(() => {
    if (!open) return undefined;

    const menu = menuRef.current;
    const toggle = toggleRef.current;
    if (!menu) return undefined;

    const getFocusable = () => {
      const inMenu = Array.from(menu.querySelectorAll(FOCUSABLE));
      return toggle ? [toggle, ...inMenu] : inMenu;
    };

    const focusable = getFocusable();
    const firstInMenu = focusable.find((el) => el !== toggle) || toggle;
    firstInMenu?.focus({ preventScroll: true });

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key !== "Tab") return;

      const items = getFocusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const activeEl = document.activeElement;

      if (e.shiftKey) {
        if (activeEl === first || !items.includes(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else if (activeEl === last || !items.includes(activeEl)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      toggle?.focus({ preventScroll: true });
    };
  }, [open]);

  const handleNav = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      ref={barRef}
      className="fixed top-0 left-0 z-[900] w-full opacity-0"
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 pt-4 md:pt-6">
        <nav
          className={`flex items-center justify-between rounded-full border border-white/10 px-5 md:px-7 py-3 backdrop-blur-xl transition-colors duration-500 ${
            scrolled ? "bg-black/60" : "bg-white/[0.03]"
          }`}
        >
          <a
            href="#top"
            onClick={(e) => handleNav(e, "#top")}
            className="inline-flex items-center gap-2.5 font-display text-lg tracking-[0.15em] text-white"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="navbar-logo"
          >
            <img
              src="/media/brand/klyne-mark.png"
              alt=""
              aria-hidden="true"
              width={32}
              height={32}
              className="h-[28px] w-[28px] object-contain md:h-8 md:w-8"
              draggable={false}
            />
            {brand.name}
          </a>

          <div className="hidden items-center gap-8 lg:flex">
            {nav.links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNav(e, link.href)}
                data-active={active === link.href}
                aria-current={active === link.href ? "true" : undefined}
                className="nav-link text-sm text-white/70 transition-colors hover:text-white"
                data-testid={`navbar-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <MagneticButton
              as="a"
              href={nav.cta.href}
              onClick={(e) => handleNav(e, nav.cta.href)}
              className="hidden rounded-full border border-white/20 px-5 py-2 text-sm text-white transition-colors hover:border-gold hover:bg-gold hover:text-black sm:inline-flex"
              data-testid="navbar-cta"
            >
              {nav.cta.label}
            </MagneticButton>
            <button
              ref={toggleRef}
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav-menu"
              data-testid="navbar-menu-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </nav>
      </div>

      {open && (
        <div className="mx-auto max-w-[1400px] px-6 lg:hidden">
          <div
            id="mobile-nav-menu"
            ref={menuRef}
            role="navigation"
            aria-label="Mobile"
            className="glass-surface mt-3 rounded-3xl border border-white/10 p-6"
          >
            <div className="flex flex-col gap-4">
              {nav.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNav(e, link.href)}
                  className="text-2xl font-display text-white/80"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  data-testid={`navbar-mobile-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href={nav.cta.href}
                onClick={(e) => handleNav(e, nav.cta.href)}
                className="cta-solid mt-2 inline-flex justify-center rounded-full bg-white px-5 py-3 text-sm text-black"
                data-testid="navbar-mobile-cta"
              >
                {nav.cta.label}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
